import { useEffect, useMemo, useState } from "react";
import { Loader2, Sparkles, Gem } from "lucide-react";
import { useActiveOrg } from "@/hooks/use-active-org";
import { useOrgSubscription } from "@/hooks/use-org-subscription";
import { useOrgPaymentMethod } from "@/hooks/use-org-payment-method";
import { useOrgQuota } from "@/hooks/use-org-quota";
import { useAddOns } from "@/hooks/use-add-ons";
import { useOrgInvoices } from "@/hooks/use-org-invoices";
import { Button } from "@/components/ui/button";
import ActiveBilling, {
  PremiumDealsCard,
  UsageCardSkeleton,
  UsageCardError,
} from "@/components/settings/org-billing-active";
import PlanGrid, { toPlanKey } from "@/components/settings/org-billing-plan-grid";
import PaymentMethodSection from "@/components/settings/org-billing-payment-method";
import type { Subscription } from "@/api/billing-api";
import { orgBillingApi } from "@/api/org-billing-api";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/api-error";

export default function OrgBilling() {
  const { subscription, isLoading } = useOrgSubscription();
  useConfirmPaymentMethodSetup();
  useConfirmCreditPackPurchase();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (subscription) {
    return <ActiveBillingPage subscription={subscription} />;
  }

  return <FreeTierBilling />;
}

function ActiveBillingPage({
  subscription,
}: {
  subscription: Subscription;
}) {
  const { orgId, orgName } = useActiveOrg();
  const { card } = useOrgPaymentMethod();
  const orgLabel = orgName ?? "your organization";

  // Prefer the canonical plan_tier from the BE (Stripe price metadata.tier).
  // Fall back to parsing the free-form price nickname for responses that predate
  // the field, so the change is safe regardless of FE/BE deploy order.
  const planKey =
    subscription.plan_tier ?? toPlanKey(subscription.items[0]?.nickname);

  return (
    <div className="mx-auto max-w-4xl px-6 py-8 space-y-10">
      <header>
        <h1 className="text-2xl font-semibold text-foreground">Billing &amp; plans</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          One subscription powers every workspace inside {orgLabel}.
        </p>
      </header>

      <ActiveBilling subscription={subscription} orgId={orgId} />

      <AddOnSection orgId={orgId} />

      <PlanGrid currentPlanKey={planKey} orgId={orgId} />

      <PaymentMethodSection card={card} orgId={orgId} />

      <InvoicesSection />
    </div>
  );
}

// Credit-pack Checkout (mode=payment) redirects back with ?credit_session={ID}.
// On detection, post the session id to /credits/confirm to credit the balance,
// toast the new balance, then strip the param so a refresh doesn't re-fire.
function useConfirmCreditPackPurchase() {
  const { orgId } = useActiveOrg();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!orgId) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("credit_session");
    if (!sessionId) return;

    // Strip the param BEFORE the async call so React StrictMode's second
    // effect invocation reads a clean URL and bails. The BE is also
    // idempotent via credited_pack_sessions, but this avoids the redundant
    // round-trip + duplicate toast in dev.
    params.delete("credit_session");
    const next = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (next ? `?${next}` : "")
    );

    (async () => {
      try {
        const result = await orgBillingApi.confirmCreditPackPurchase(
          orgId,
          sessionId
        );
        if (result.credited) {
          toast.success(
            `Added ${result.pack_size} credit${
              result.pack_size === 1 ? "" : "s"
            }. New balance: ${result.balance}.`
          );
        }
        queryClient.invalidateQueries({ queryKey: ["orgQuota"] });
      } catch (err) {
        console.error("Failed to confirm credit pack purchase", err);
        toast.error(getErrorMessage(err, "Couldn't finish credit purchase"));
      }
    })();
  }, [orgId, queryClient]);
}

// Stripe Checkout (mode=setup) redirects back with ?setup_session={ID}. On
// detection, post the session id to /payment-method/confirm to promote the
// new card to default, then strip the query param so a refresh doesn't re-fire.
function useConfirmPaymentMethodSetup() {
  const { orgId } = useActiveOrg();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!orgId) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("setup_session");
    if (!sessionId) return;

    (async () => {
      try {
        await orgBillingApi.confirmPaymentMethodSetup(orgId, sessionId);
        toast.success("Card updated");
        queryClient.invalidateQueries({ queryKey: ["orgPaymentMethod"] });
      } catch (err) {
        console.error("Failed to confirm card setup", err);
        toast.error(getErrorMessage(err, "Couldn't finish card update"));
      } finally {
        params.delete("setup_session");
        const next = params.toString();
        window.history.replaceState(
          {},
          "",
          window.location.pathname + (next ? `?${next}` : "")
        );
      }
    })();
  }, [orgId, queryClient]);
}

function FreeTierBilling() {
  const { orgId, orgName } = useActiveOrg();
  const { card } = useOrgPaymentMethod();
  const orgLabel = orgName ?? "your organization";

  const scrollToPlanGrid = () => {
    document
      .getElementById("plan-grid")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-8 space-y-10">
      <header>
        <h1 className="text-2xl font-semibold text-foreground">Billing &amp; plans</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          One subscription powers every workspace inside {orgLabel}. Choose a plan
          to unlock more workspaces, premium deals, and team features.
        </p>
      </header>

      <NoActiveSubscriptionCard onChoosePlan={scrollToPlanGrid} />

      <FreeTierUsage />

      <AddOnSection orgId={orgId} />

      <PlanGrid orgId={orgId} />

      <PaymentMethodSection card={card} orgId={orgId} />

      <InvoicesSection />
    </div>
  );
}



function NoActiveSubscriptionCard({ onChoosePlan }: { onChoosePlan: () => void }) {
  return (
    <section className="rounded-lg border border-border bg-card p-6">
      <h2 className="text-base font-semibold text-foreground">
        No active subscription
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        You&apos;re on the Free tier — 1 workspace, unlimited deals with 1 premium
        deal included.
      </p>
      <div className="mt-4">
        <Button onClick={onChoosePlan}>Choose a plan</Button>
      </div>
    </section>
  );
}

// Free-tier orgs have no subscription, so the paid-plan usage cards never
// render. Surface the premium-deals card on its own so add-on credits the user
// just purchased are actually visible.
function FreeTierUsage() {
  const { quota, isLoading, error } = useOrgQuota();

  if (isLoading) {
    return (
      <section className="grid gap-4 md:grid-cols-3">
        <UsageCardSkeleton
          label="Premium deals, shared org-wide"
          icon={<Gem className="h-4 w-4 text-muted-foreground" />}
        />
      </section>
    );
  }

  if (error || !quota) {
    return <UsageCardError />;
  }

  return (
    <section className="grid gap-4 md:grid-cols-3">
      <PremiumDealsCard quota={quota} />
    </section>
  );
}

function AddOnSection({ orgId }: { orgId?: string }) {
  const { addOns, isLoading } = useAddOns();
  const [isPending, setIsPending] = useState<number | null>(null);

  const handleBuy = async (packSize: number) => {
    if (!orgId) return;
    setIsPending(packSize);
    try {
      // Stripe substitutes the literal `{CHECKOUT_SESSION_ID}` placeholder on
      // redirect — do NOT use URL.searchParams.set, which percent-encodes the
      // braces and breaks substitution.
      const base = `${window.location.origin}${window.location.pathname}`;
      const successUrl = `${base}?credit_session={CHECKOUT_SESSION_ID}`;
      const cancelUrl = base;
      const session = await orgBillingApi.startCreditPackCheckout(
        orgId,
        packSize,
        successUrl,
        cancelUrl
      );
      window.location.href = session.checkout_url;
    } catch (err) {
      console.error("Credit pack checkout failed", err);
      toast.error(getErrorMessage(err, "Couldn't start checkout."));
      setIsPending(null);
    }
  };

  if (isLoading && addOns.length === 0) {
    return (
      <section className="flex h-32 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </section>
    );
  }

  if (addOns.length === 0) return null;

  // Use the smallest pack's per-credit price as the baseline for savings
  // labels on larger packs.
  const baseline = addOns[0].per_credit_cents;

  return (
    <section>
      <h2 className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground/70">
        Add-on premium deals
      </h2>
      <div className="mt-3 grid gap-4 md:grid-cols-3">
        {addOns.map((addon) => {
          const pricePerCredit = Math.round(addon.per_credit_cents / 100);
          const total = Math.round(addon.unit_amount_cents / 100);
          const savingsPct =
            addon.per_credit_cents < baseline
              ? Math.round((1 - addon.per_credit_cents / baseline) * 100)
              : null;
          return (
            <div
              key={addon.pack_size}
              className="flex flex-col rounded-lg border border-border bg-card p-5"
            >
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground">
                  {addon.pack_size} {addon.pack_size === 1 ? "deal" : "deals"}
                </h3>
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-2xl font-semibold text-foreground">
                  ${pricePerCredit}
                </span>
                <span className="text-sm text-muted-foreground">/each</span>
              </div>
              <p className="text-xs text-muted-foreground">
                ${total} total
                {savingsPct !== null && (
                  <span className="ml-1.5 text-emerald-600">
                    Save {savingsPct}%
                  </span>
                )}
              </p>
              <div className="mt-auto pt-5">
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={!orgId || isPending === addon.pack_size}
                  onClick={() => handleBuy(addon.pack_size)}
                >
                  {isPending === addon.pack_size ? "Starting…" : "Buy pack"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function InvoicesSection() {
  const { invoices, isLoading } = useOrgInvoices();

  const formatDate = (unix: number) =>
    new Date(unix * 1000).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const formatAmount = (cents: number, currency: string) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.toUpperCase(),
      maximumFractionDigits: 0,
    }).format(cents / 100);

  // Stripe line descriptions are auto-generated like "1 × Elite (at $8,388.00
  // / year)". The "(at $… / period)" suffix duplicates the Amount column and
  // a "1 ×" prefix is noise when the quantity is one. Strip both so the
  // column reads "Elite" / "Pro plan" / "10 × Premium deal credits" etc.
  const cleanItem = (raw: string | null | undefined): string => {
    if (!raw) return "—";
    const withoutPrice = raw.replace(/\s*\(at\s+[^)]+\)\s*$/i, "");
    return withoutPrice.replace(/^1\s*[×x]\s*/i, "");
  };

  // Derive cadence from the line's billing period. More reliable than parsing
  // the Stripe-generated description. Returns null for one-off purchases
  // (credit packs — period start == end) and pro-rated partials we cannot
  // confidently bucket.
  const periodLabel = (start?: number, end?: number): string | null => {
    if (!start || !end || end <= start) return null;
    const days = (end - start) / 86_400;
    if (days >= 350) return "Annual";
    if (days >= 170 && days <= 200) return "Semi-Annual";
    if (days >= 80 && days <= 100) return "Quarterly";
    if (days >= 28 && days <= 35) return "Monthly";
    return null;
  };

  const rows = useMemo(
    () =>
      invoices.map((inv) => {
        const firstLine = cleanItem(inv.lines[0]?.description);
        const cadence = periodLabel(
          inv.lines[0]?.period?.start,
          inv.lines[0]?.period?.end
        );
        const extraLines = inv.lines.length - 1;
        return {
          id: inv.id,
          date: formatDate(inv.created),
          item: cadence ? `${firstLine} (${cadence})` : firstLine,
          extraLines,
          amount: formatAmount(inv.amount_paid || inv.amount_due, inv.currency),
          status: inv.status,
          hostedUrl: inv.hosted_invoice_url,
          pdf: inv.invoice_pdf,
        };
      }),
    [invoices]
  );

  if (isLoading) {
    return (
      <section>
        <h2 className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground/70">
          Invoices
        </h2>
        <div className="mt-3 flex h-24 items-center justify-center rounded-lg border border-border bg-card">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </section>
    );
  }

  if (invoices.length === 0) {
    return (
      <section>
        <h2 className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground/70">
          Invoices
        </h2>
        <div className="mt-3 rounded-lg border border-dashed border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No invoices yet — they&apos;ll appear here once you start a paid plan.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section>
      <h2 className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground/70">
        Invoices
      </h2>
      {/* Mobile: stacked cards */}
      <div className="mt-3 space-y-2 sm:hidden">
        {rows.map((row) => (
          <div
            key={row.id}
            className="rounded-lg border border-border bg-card p-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 text-foreground">
                {row.item}
                {row.extraLines > 0 && (
                  <span className="ml-1 text-xs text-muted-foreground">
                    (+{row.extraLines} more)
                  </span>
                )}
              </div>
              <span className="shrink-0 font-medium text-foreground">
                {row.amount}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between gap-3 text-xs text-muted-foreground">
              <span>
                {row.date} · <span className="capitalize">{row.status}</span>
              </span>
              <span className="flex shrink-0 items-center gap-3">
                {row.hostedUrl && (
                  <a
                    href={row.hostedUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-foreground underline-offset-4 hover:underline"
                  >
                    View
                  </a>
                )}
                {row.pdf && (
                  <a
                    href={row.pdf}
                    target="_blank"
                    rel="noreferrer"
                    className="text-foreground underline-offset-4 hover:underline"
                  >
                    PDF
                  </a>
                )}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: table */}
      <div className="mt-3 hidden overflow-hidden rounded-lg border border-border bg-card sm:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30 text-left text-xs text-muted-foreground">
              <th className="px-4 py-2 font-medium">Date</th>
              <th className="px-4 py-2 font-medium">Item</th>
              <th className="px-4 py-2 font-medium">Amount</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium text-right">Receipt</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-b border-border last:border-0">
                <td className="px-4 py-2 text-foreground align-top">
                  {row.date}
                </td>
                <td className="px-4 py-2 align-top">
                  <div className="text-foreground">
                    {row.item}
                    {row.extraLines > 0 && (
                      <span className="ml-1 text-xs text-muted-foreground">
                        (+{row.extraLines} more)
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2 text-foreground align-top">
                  {row.amount}
                </td>
                <td className="px-4 py-2 text-muted-foreground capitalize align-top">
                  {row.status}
                </td>
                <td className="px-4 py-2 text-right align-top">
                  {row.hostedUrl && (
                    <a
                      href={row.hostedUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-foreground underline-offset-4 hover:underline"
                    >
                      View
                    </a>
                  )}
                  {row.pdf && (
                    <a
                      href={row.pdf}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-3 text-foreground underline-offset-4 hover:underline"
                    >
                      PDF
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
